import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../supabaseClient';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import LeasingCalculator from '../acheter/components/LeasingCalculator';
import { useAutoSync } from '../../hooks/useAutoSync';
import SeoHead from '../../components/feature/SeoHead';

interface Vehicle {
  id: string;
  brand: string;
  model: string;
  year: number;
  price: number;
  mileage: number;
  fuel: string;
  fuel_type?: string;
  transmission: string;
  power?: string;
  color: string;
  image_url?: string;
  images?: string[];
  features: string[];
  description?: string;
  status: string;
}

interface AutoScoutPreview {
  title: string;
  price: string;
  imageUrl?: string;
  images?: string[];
  mileage?: string;
  year?: string;
  fuel?: string;
  transmission?: string;
  power?: string;
}

export default function VehiculesPage() {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [showCalculator, setShowCalculator] = useState(false);
  const [fuelFilter, setFuelFilter] = useState<string>('all');
  const [transmissionFilter, setTransmissionFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('price-asc');
  const [selectedImageIndex, setSelectedImageIndex] = useState<{ [key: string]: number }>({});
  const [autoScoutPreviews, setAutoScoutPreviews] = useState<{ [key: string]: AutoScoutPreview }>({});
  const [loadingPreviews, setLoadingPreviews] = useState<{ [key: string]: boolean }>({});

  const { syncNow } = useAutoSync(5);
  const [lastSync, setLastSync] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const isMountedRef = useRef(true);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.louatiautocenter.ch';

  const fetchAutoScoutPreview = async (url: string, vehicleId: string) => {
    if (!url || loadingPreviews[vehicleId] || autoScoutPreviews[vehicleId]) return;
    setLoadingPreviews(prev => ({ ...prev, [vehicleId]: true }));
    try {
      const { data, error } = await supabase.functions.invoke('fetch-autoscout-preview', {
        body: { url }
      });
      if (error) throw error;
      if (data && isMountedRef.current) {
        setAutoScoutPreviews(prev => ({ ...prev, [vehicleId]: data }));
      }
    } catch (error) {
      console.error('Erreur preview AutoScout24:', error);
    } finally {
      if (isMountedRef.current) {
        setLoadingPreviews(prev => ({ ...prev, [vehicleId]: false }));
      }
    }
  };

  const fetchVehicles = async () => {
    try {
      const { data, error } = await supabase
        .from('vehicles')
        .select('*')
        .eq('status', 'available')
        .order('created_at', { ascending: false });
      if (error) throw error;
      if (isMountedRef.current) {
        setVehicles(data || []);
        data?.forEach(vehicle => {
          const autoscoutUrl = (vehicle as any).autoscout_url;
          if (autoscoutUrl) fetchAutoScoutPreview(autoscoutUrl, vehicle.id);
        });
      }
    } catch (error) {
      console.error('Erreur chargement véhicules:', error);
      if (isMountedRef.current) setVehicles([]);
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    const stored = localStorage.getItem('lastSync');
    if (stored) setLastSync(stored);
    fetchVehicles();
    return () => { isMountedRef.current = false; };
  }, []);

  const handleManualSync = async () => {
    setIsSyncing(true);
    try {
      await syncNow();
      const now = new Date().toISOString();
      if (isMountedRef.current) {
        setLastSync(now);
        localStorage.setItem('lastSync', now);
        setLoading(true);
        setAutoScoutPreviews({});
        await fetchVehicles();
      }
    } catch (error) {
      console.error('Erreur sync:', error);
    } finally {
      if (isMountedRef.current) setIsSyncing(false);
    }
  };

  const formatLastSync = (dateString: string | null) => {
    if (!dateString) return 'Jamais';
    const date = new Date(dateString);
    const diffMins = Math.floor((Date.now() - date.getTime()) / 60000);
    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    return date.toLocaleDateString('fr-FR');
  };

  const getFuel = (vehicle: Vehicle) => vehicle.fuel || vehicle.fuel_type || 'Non spécifié';

  // Récupère l'image principale : priorité à image_url personnalisée, puis preview AutoScout
  const getAutoScoutImage = (vehicle: Vehicle & { autoscout_url?: string; image_url?: string }, vehicleId: string): string | null => {
    // Priorité 1 : photo uploadée manuellement
    if (vehicle.image_url) return vehicle.image_url;
    // Priorité 2 : preview AutoScout24
    const preview = autoScoutPreviews[vehicleId];
    if (!preview) return null;
    if (preview.images && preview.images.length > 0) {
      const idx = selectedImageIndex[vehicleId] || 0;
      return preview.images[idx] || preview.images[0];
    }
    if (preview.imageUrl) return preview.imageUrl;
    return null;
  };

  const getMainImage = (vehicle: Vehicle, index: number = 0) => {
    if (vehicle.images && vehicle.images.length > 0) {
      return vehicle.images[index] || vehicle.images[0];
    }
    return vehicle.image_url || 'https://public.readdy.ai/ai/img_res/2b456a76260954ad955927041420cd4b.jpg';
  };

  const handleImageChange = (vehicleId: string, direction: 'prev' | 'next', totalImages: number) => {
    if (!isMountedRef.current) return;
    setSelectedImageIndex(prev => {
      const currentIndex = prev[vehicleId] || 0;
      const newIndex = direction === 'next'
        ? (currentIndex + 1) % totalImages
        : currentIndex === 0 ? totalImages - 1 : currentIndex - 1;
      return { ...prev, [vehicleId]: newIndex };
    });
  };

  const filteredVehicles = vehicles
    .filter(v => fuelFilter === 'all' || getFuel(v) === fuelFilter)
    .filter(v => transmissionFilter === 'all' || v.transmission === transmissionFilter)
    .sort((a, b) => {
      switch (sortBy) {
        case 'price-asc': return a.price - b.price;
        case 'price-desc': return b.price - a.price;
        case 'year-desc': return b.year - a.year;
        case 'mileage-asc': return a.mileage - b.mileage;
        default: return 0;
      }
    });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1e]">
        <SeoHead
          title="Véhicules d'Occasion à Vendre – Stock Payerne | Louati AUTOCENTER"
          description="Découvrez notre stock de véhicules d'occasion à Payerne. Voitures sélectionnées, garanties et préparées par Louati AUTOCENTER. Financement leasing disponible."
          keywords="véhicules occasion Payerne, voiture d'occasion Suisse, achat voiture Payerne, stock auto Louati"
          canonical={`${siteUrl}/vehicules`}
        />
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-400">Chargement des véhicules...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1e]">
      <SeoHead
        title="Véhicules d'Occasion à Vendre – Stock Payerne | Louati AUTOCENTER"
        description="Découvrez notre stock de véhicules d'occasion à Payerne. Voitures sélectionnées, garanties et préparées par Louati AUTOCENTER."
        keywords="véhicules occasion Payerne, voiture d'occasion Suisse, achat voiture Payerne, stock auto Louati"
        canonical={`${siteUrl}/vehicules`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Véhicules d'Occasion à Vendre – Stock Payerne | Louati AUTOCENTER",
            "description": "Découvrez notre stock de véhicules d'occasion à Payerne. Voitures sélectionnées, garanties et préparées par Louati AUTOCENTER.",
            "url": `${siteUrl}/vehicules`,
            "inLanguage": "fr-CH",
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Accueil", "item": `${siteUrl}/` },
                { "@type": "ListItem", "position": 2, "name": "Véhicules", "item": `${siteUrl}/vehicules` }
              ]
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "Véhicules d'occasion disponibles",
            "description": "Stock de véhicules d'occasion chez Louati AUTOCENTER à Payerne",
            "url": `${siteUrl}/vehicules`
          }
        ]}
      />
      <Navbar />

      {/* Hero */}
      <div className="relative pt-24 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a0f1e] via-[#0d1530] to-[#0a0f1e]"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-blue-700 rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-500/10 border border-blue-500/30 rounded-full text-blue-400 text-sm mb-4">
                <i className="ri-car-line"></i>
                <span>{filteredVehicles.length} véhicule{filteredVehicles.length > 1 ? 's' : ''} disponible{filteredVehicles.length > 1 ? 's' : ''}</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">Véhicules <span className="text-blue-400">Disponibles</span></h1>
              <p className="text-gray-400 text-lg">Découvrez notre sélection de véhicules d'exception</p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <button
                onClick={handleManualSync}
                disabled={isSyncing}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer text-sm"
              >
                <i className={`ri-refresh-line ${isSyncing ? 'animate-spin' : ''}`}></i>
                {isSyncing ? 'Synchronisation...' : 'Synchroniser'}
              </button>
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <i className="ri-time-line"></i>Dernière sync: {formatLastSync(lastSync)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">

        {vehicles.length === 0 ? (
          <div className="py-20 text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-blue-500/10 rounded-full mx-auto mb-6">
              <i className="ri-car-line text-4xl text-blue-400"></i>
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Aucun véhicule disponible actuellement</h3>
            <p className="text-gray-400 mb-8">Nous mettons régulièrement à jour notre stock. Contactez-nous pour plus d'informations.</p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a href="tel:+41796840588" className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-phone-line"></i>Nous appeler
              </a>
              <a href="https://wa.me/41796840588" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-whatsapp-line"></i>WhatsApp
              </a>
            </div>
          </div>
        ) : (
          <>
            {/* Filters */}
            <div className="mb-8 p-4 bg-white/5 border border-white/10 rounded-xl backdrop-blur-sm">
              <div className="flex flex-wrap gap-3 items-center justify-between">
                <div className="flex flex-wrap gap-3">
                  <select
                    value={fuelFilter}
                    onChange={(e) => setFuelFilter(e.target.value)}
                    className="px-4 py-2 bg-white/10 border border-white/20 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer text-sm"
                  >
                    <option value="all" className="bg-gray-900">Tous les carburants</option>
                    <option value="Essence" className="bg-gray-900">Essence</option>
                    <option value="Diesel" className="bg-gray-900">Diesel</option>
                    <option value="Hybride" className="bg-gray-900">Hybride</option>
                    <option value="Électrique" className="bg-gray-900">Électrique</option>
                  </select>
                  <select
                    value={transmissionFilter}
                    onChange={(e) => setTransmissionFilter(e.target.value)}
                    className="px-4 py-2 bg-white/10 border border-white/20 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer text-sm"
                  >
                    <option value="all" className="bg-gray-900">Toutes les transmissions</option>
                    <option value="Automatique" className="bg-gray-900">Automatique</option>
                    <option value="Manuelle" className="bg-gray-900">Manuelle</option>
                  </select>
                </div>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-2 bg-white/10 border border-white/20 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer text-sm"
                >
                  <option value="price-asc" className="bg-gray-900">Prix croissant</option>
                  <option value="price-desc" className="bg-gray-900">Prix décroissant</option>
                  <option value="year-desc" className="bg-gray-900">Plus récent</option>
                  <option value="mileage-asc" className="bg-gray-900">Kilométrage croissant</option>
                </select>
              </div>
            </div>

            {/* Vehicles Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredVehicles.map((vehicle) => {
                const isAutoScout = !!(vehicle as any).autoscout_url;

                if (isAutoScout) {
                  const preview = autoScoutPreviews[vehicle.id];
                  const isLoadingPreview = loadingPreviews[vehicle.id];
                  const autoScoutImage = getAutoScoutImage(vehicle as any, vehicle.id);
                  // Titre : priorité au champ brand saisi manuellement (si différent de 'AutoScout24'), sinon preview title
                  const displayTitle = (vehicle.brand && vehicle.brand !== 'AutoScout24')
                    ? vehicle.brand
                    : (preview?.title || 'Véhicule AutoScout24');
                  // Prix : priorité au prix saisi manuellement
                  const displayPrice = vehicle.price > 0
                    ? `CHF ${vehicle.price.toLocaleString()}`
                    : (preview?.price || null);

                  return (
                    <div key={vehicle.id} className="bg-[#111827] rounded-xl overflow-hidden border border-white/10 hover:border-blue-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/10 group">
                      {/* Header badge */}
                      <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-4 py-2.5 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <i className="ri-car-line text-white"></i>
                          <span className="text-white font-semibold text-sm">AutoScout24</span>
                        </div>
                        <span className="px-2 py-0.5 bg-white/20 text-white text-xs font-semibold rounded-full">
                          {vehicle.status === 'available' ? 'Disponible' : vehicle.status === 'sold' ? 'Vendu' : 'Réservé'}
                        </span>
                      </div>

                      {/* Image */}
                      <div className="relative h-56 w-full overflow-hidden bg-[#0d1530]">
                        {autoScoutImage ? (
                          <img
                            src={autoScoutImage}
                            alt={displayTitle}
                            className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : isLoadingPreview ? (
                          <div className="w-full h-full flex items-center justify-center">
                            <div className="text-center">
                              <div className="w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                              <p className="text-xs text-gray-500">Chargement...</p>
                            </div>
                          </div>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <div className="text-center text-gray-600">
                              <i className="ri-image-line text-4xl mb-2"></i>
                              <p className="text-xs">Image non disponible</p>
                            </div>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-[#111827]/80 via-transparent to-transparent"></div>
                      </div>

                      {/* Content */}
                      <div className="p-5">
                        <h3 className="text-lg font-bold text-white mb-1 leading-tight">{displayTitle}</h3>
                        {displayPrice && (
                          <p className="text-2xl font-bold text-blue-400 mb-3">{displayPrice}</p>
                        )}
                        {preview && (
                          <div className="flex flex-wrap gap-3 text-xs text-gray-400 mb-4">
                            {preview.year && <span className="flex items-center gap-1"><i className="ri-calendar-line text-blue-400"></i>{preview.year}</span>}
                            {preview.mileage && <span className="flex items-center gap-1"><i className="ri-dashboard-line text-blue-400"></i>{preview.mileage}</span>}
                            {preview.fuel && <span className="flex items-center gap-1"><i className="ri-gas-station-line text-blue-400"></i>{preview.fuel}</span>}
                            {preview.transmission && <span className="flex items-center gap-1"><i className="ri-settings-3-line text-blue-400"></i>{preview.transmission}</span>}
                          </div>
                        )}
                        <a
                          href={(vehicle as any).autoscout_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold text-sm cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-external-link-line"></i>
                          Voir sur AutoScout24
                        </a>
                      </div>
                    </div>
                  );
                }

                // Véhicule manuel
                const currentImageIndex = selectedImageIndex[vehicle.id] || 0;
                const totalImages = vehicle.images?.length || 1;
                const hasMultipleImages = totalImages > 1;

                return (
                  <div key={vehicle.id} className="bg-[#111827] rounded-xl overflow-hidden border border-white/10 hover:border-blue-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/10 group">
                    <div className="relative h-56 w-full overflow-hidden bg-[#0d1530]">
                      <img
                        src={getMainImage(vehicle, currentImageIndex)}
                        alt={`${vehicle.brand} ${vehicle.model}`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#111827]/80 via-transparent to-transparent"></div>
                      <div className="absolute top-3 right-3 bg-blue-600 text-white px-2.5 py-1 rounded-full text-xs font-bold">
                        {vehicle.year}
                      </div>
                      {hasMultipleImages && (
                        <>
                          <button onClick={() => handleImageChange(vehicle.id, 'prev', totalImages)} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                            <i className="ri-arrow-left-s-line"></i>
                          </button>
                          <button onClick={() => handleImageChange(vehicle.id, 'next', totalImages)} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                            <i className="ri-arrow-right-s-line"></i>
                          </button>
                          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1">
                            {Array.from({ length: totalImages }).map((_, idx) => (
                              <div key={idx} className={`h-1.5 rounded-full transition-all ${idx === currentImageIndex ? 'bg-white w-4' : 'bg-white/40 w-1.5'}`}></div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>

                    <div className="p-5">
                      <h3 className="text-lg font-bold text-white mb-1">{vehicle.brand} {vehicle.model}</h3>
                      <div className="flex flex-wrap gap-3 text-xs text-gray-400 mb-3">
                        <span className="flex items-center gap-1"><i className="ri-gas-station-line text-blue-400"></i>{getFuel(vehicle)}</span>
                        <span className="flex items-center gap-1"><i className="ri-settings-3-line text-blue-400"></i>{vehicle.transmission}</span>
                        <span className="flex items-center gap-1"><i className="ri-dashboard-line text-blue-400"></i>{vehicle.mileage.toLocaleString()} km</span>
                      </div>
                      {vehicle.features && vehicle.features.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-4">
                          {vehicle.features.slice(0, 3).map((feature, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-blue-500/10 text-blue-400 text-xs rounded-full border border-blue-500/20 whitespace-nowrap">{feature}</span>
                          ))}
                          {vehicle.features.length > 3 && (
                            <span className="px-2 py-0.5 bg-white/5 text-gray-400 text-xs rounded-full whitespace-nowrap">+{vehicle.features.length - 3}</span>
                          )}
                        </div>
                      )}
                      <div className="border-t border-white/10 pt-4 flex items-center justify-between">
                        <span className="text-2xl font-bold text-blue-400">CHF {vehicle.price.toLocaleString()}</span>
                        <button
                          onClick={() => { if (isMountedRef.current) { setSelectedVehicle(vehicle); setShowCalculator(true); } }}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold text-sm whitespace-nowrap cursor-pointer"
                        >
                          Calculer leasing
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {showCalculator && selectedVehicle && (
        <LeasingCalculator vehicle={selectedVehicle} onClose={() => { if (isMountedRef.current) { setShowCalculator(false); setSelectedVehicle(null); } }} />
      )}

      <Footer />
    </div>
  );
}
